import React from 'react';
import classnames from 'classnames';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PreviewOutlined } from '@mui/icons-material';
import { FormControlLabelDense } from '@upstart/commander-components';
import {
  CheckboxSrcType,
  ShareDealDialogSelectedStickerOffers,
  ShareDealDialogStickerConfig,
} from './types';
import messages from '../../messages.json';
import styles from './Content.module.scss';
import { hasLeaseTermAndMileageError } from '../../validation';

interface StickerConfigSectionProps<
  V extends CheckboxSrcType,
  DateType = Date,
> {
  config: ShareDealDialogStickerConfig<V, DateType>;
  onChange: (newConfig: ShareDealDialogStickerConfig<V, DateType>) => void;
  onPreviewStickerClick: () => void;
}

const StickerConfigSectionContent = <
  V extends CheckboxSrcType,
  DateType = Date,
>({
  config,
  onChange,
}: Exclude<
  StickerConfigSectionProps<V, DateType>,
  'onPreviewStickerClick'
>) => {
  const { offersSource, selectedOffers } = config;

  const handleCheckbox = (
    offerType: keyof ShareDealDialogSelectedStickerOffers<V>,
    value: V,
    isChecked: boolean,
  ) => {
    const fieldToUpdate = selectedOffers[offerType];
    if (typeof fieldToUpdate === 'boolean') {
      onChange({
        ...config,
        selectedOffers: { ...config.selectedOffers, [offerType]: isChecked },
      });
      return;
    }

    const newArray = isChecked
      ? [...fieldToUpdate, value]
      : fieldToUpdate.filter((v) => v !== value);
    onChange({
      ...config,
      selectedOffers: { ...config.selectedOffers, [offerType]: newArray },
    });
  };

  const hasLease =
    offersSource.leaseMileages.length > 0 || offersSource.leaseTerms.length > 0;
  // TODO: integrate this validation into react-hook-form
  const hasLeaseError = hasLeaseTermAndMileageError(config);

  const getLeaseGridItems = () => (
    <>
      <Typography
        variant="h6"
        id="dc-send-deal-lease-checkboxes-header"
        className={`${styles.checkboxGroupHeader} ${styles.leaseCheckboxHeader}`}
      >
        {messages.formStep.lease}
      </Typography>
      <div
        className={`${styles.leaseCheckboxes} ${styles.checkboxGroup}`}
        role="group"
        aria-labelledby="dc-send-deal-lease-checkboxes-header"
      >
        <FormControl>
          <FormGroup>
            {offersSource.leaseMileages.map((src) => (
              <FormControlLabelDense
                key={src.value}
                value={src.value}
                label={src.label}
                control={
                  <Checkbox
                    color="secondary"
                    checked={selectedOffers.leaseMileages.includes(src.value)}
                    onChange={(e) =>
                      handleCheckbox(
                        'leaseMileages',
                        src.value,
                        e.target.checked,
                      )
                    }
                  />
                }
              />
            ))}
          </FormGroup>
        </FormControl>
        <FormControl>
          <FormGroup>
            {offersSource.leaseTerms.map((src) => (
              <FormControlLabelDense
                key={src.value}
                value={src.value}
                label={src.label}
                control={
                  <Checkbox
                    color="secondary"
                    checked={selectedOffers.leaseTerms.includes(src.value)}
                    onChange={(e) =>
                      handleCheckbox('leaseTerms', src.value, e.target.checked)
                    }
                  />
                }
              />
            ))}
          </FormGroup>
        </FormControl>
        {hasLeaseError && (
          <FormHelperText error>
            {messages.formStep.leaseTermAndMileageError}
          </FormHelperText>
        )}
      </div>
    </>
  );

  const hasPurchase = offersSource.purchases.length > 0;
  const getPurchaseGridItems = () => (
    <>
      <Typography
        variant="h6"
        id="dc-send-deal-purchase-checkboxes-header"
        className={`${styles.checkboxGroupHeader} ${styles.purchaseCheckboxHeader}`}
      >
        {messages.formStep.purchase}
      </Typography>
      <div
        className={`${styles.purchaseCheckboxes} ${styles.checkboxGroup}`}
        role="group"
        aria-labelledby="dc-send-deal-purchase-checkboxes-header"
      >
        <FormGroup>
          {offersSource.purchases.map((src) => (
            <FormControlLabelDense
              key={src.value}
              value={src.value}
              label={src.label}
              control={
                <Checkbox
                  color="secondary"
                  checked={selectedOffers.purchases.includes(src.value)}
                  onChange={(e) =>
                    handleCheckbox('purchases', src.value, e.target.checked)
                  }
                />
              }
            />
          ))}
        </FormGroup>
      </div>
    </>
  );

  return (
    <>
      <div className={styles.paddingBottomLg}>
        <Typography
          variant="h6"
          fontWeight={700}
          className={`${styles.paddingBottomMd} ${styles.paddingTopMd}`}
        >
          {messages.formStep.selectPurchaseTypesHeader}
        </Typography>

        <div
          className={classnames({
            [styles.stickerPurchaseTypeGrid]: true,
            [styles.withoutPurchaseCheckboxes]: !hasPurchase,
          })}
        >
          {hasPurchase && getPurchaseGridItems()}

          {offersSource.cash !== null && (
            <div className={styles.cashCheckbox}>
              {/* when only showing cash + lease this checkbox will be in a grid cell much
                taller than needed. In that case we dont want the checkboxGroup styling to
                hug the content and not grow */}
              <FormGroup className={styles.checkboxGroup}>
                <FormControlLabelDense
                  value={offersSource.cash.value}
                  label={offersSource.cash.label}
                  control={
                    <Checkbox
                      color="secondary"
                      checked={selectedOffers.cash}
                      onChange={(e) =>
                        handleCheckbox(
                          'cash',
                          offersSource.cash!.value,
                          e.target.checked,
                        )
                      }
                    />
                  }
                />
              </FormGroup>
            </div>
          )}

          {hasLease && getLeaseGridItems()}
        </div>
      </div>

      <div className={styles.paddingBottomMd}>
        <Typography variant="h6" className={styles.paddingBottomMd}>
          {messages.formStep.disclaimerHeader}
        </Typography>
        <TextField
          variant="outlined"
          fullWidth
          label={messages.formStep.disclaimerTextboxLabel}
          value={config.disclaimer}
          onChange={(e) => {
            onChange({ ...config, disclaimer: e.target.value });
          }}
        />

        <div>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            className={`${styles.paddingBottomMd} ${styles.paddingTopLg}`}
          >
            {messages.formStep.offerExpirationHeader}
          </Typography>
          <DatePicker
            value={config.expirationDate}
            minDate={config.minExpirationDate}
            onChange={(date) => {
              onChange({
                ...config,
                expirationDate: date,
              });
            }}
          />
        </div>
      </div>
    </>
  );
};

const StickerConfigSection = <V extends CheckboxSrcType, DateType = Date>(
  props: StickerConfigSectionProps<V, DateType>,
) => {
  const { config, onChange, onPreviewStickerClick } = props;
  const { includeWhenSending, disableSection } = config;
  const emitOnPreviewStickerClickIfNoErrors = () => {
    if (!hasLeaseTermAndMileageError(config)) {
      onPreviewStickerClick();
    }
  };
  return (
    <Paper
      className={`${styles.contentSection} ${styles.controlGroup}`}
      elevation={0}
    >
      <div className={styles.stickerConfigSectionHeader}>
        <FormControlLabel
          label={messages.formStep.stickerSwitchLabel}
          control={
            <Switch
              color="secondary"
              checked={includeWhenSending && !disableSection}
              disabled={disableSection}
              onChange={(e) => {
                onChange({ ...config, includeWhenSending: e.target.checked });
              }}
            />
          }
        />
        {includeWhenSending && !disableSection && (
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            endIcon={<PreviewOutlined />}
            onClick={emitOnPreviewStickerClickIfNoErrors}
          >
            {messages.formStep.previewSticker}
          </Button>
        )}
      </div>

      {includeWhenSending && !disableSection && (
        <StickerConfigSectionContent {...props} />
      )}
    </Paper>
  );
};
export default StickerConfigSection;
